import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  FaArrowLeft, FaArrowRight, FaWhatsapp,
  FaRoute, FaTicketAlt, FaHotel, FaPassport,
  FaCheckCircle, FaQuoteLeft, FaStar, FaMapMarkerAlt, FaImages,
} from 'react-icons/fa';
import {
  heroSlides, stats, services, hajjPackages, hajjCampaign,
  umrahPackages, testimonials, WA_LINK, COMPANY,
} from '../../data/data';
import umrahKaaba from '../../assets/images/umrah_pilgrimage_makkah_1791488683105.jpg';
import masjidNew  from '../../assets/masjibNew.jpg';
import medina     from '../../assets/medina.jpg';
import masjid     from '../../assets/masjid.jpg';
import paris      from '../../assets/paris.jpg';
import client1    from '../../assets/client1.jpg';
import client2    from '../../assets/client2.jpg';
import './Home.css';

const SERVICE_ICONS = { FaRoute, FaTicketAlt, FaHotel, FaPassport };
const SLIDE_IMAGES  = [umrahKaaba, masjidNew, medina, masjid, paris];
const CLIENT_IMGS   = [client1, client2];

// ─── Hero Carousel ───────────────────────────────────────────
function HeroCarousel() {
  const [current, setCurrent]     = useState(0);
  const [paused,  setPaused]      = useState(false);

  const next = useCallback(() => setCurrent(c => (c + 1) % heroSlides.length), []);
  const prev = () => setCurrent(c => (c - 1 + heroSlides.length) % heroSlides.length);

  useEffect(() => {
    if (paused) return;
    const t = setTimeout(next, 3800);
    return () => clearTimeout(t);
  }, [current, paused, next]);

  const slide = heroSlides[current];

  return (
    <section
      className="hero"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      {/* Background images */}
      {heroSlides.map((s, i) => (
        <div
          key={i}
          className={`hero__bg ${i === current ? 'hero__bg--active' : ''}`}
          style={{ backgroundImage: `url(${SLIDE_IMAGES[i]})` }}
        />
      ))}
      <div className="hero__overlay" />

      {/* Slide content */}
      <div className="hero__content container">
        <AnimatePresence mode="wait">
          <motion.div
            key={current}
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{   opacity: 0, y: -20 }}
            transition={{ duration: 0.55 }}
            className="hero__text"
          >
            <span className="hero__tag">{slide.tag}</span>
            <h1 className="hero__title">{slide.title}</h1>
            <p  className="hero__subtitle">{slide.subtitle}</p>
            <div className="hero__actions">
              {slide.title.toLowerCase().includes('umrah') ? (
                <>
                  <a
                    href={`https://api.whatsapp.com/send?phone=${COMPANY.whatsapp}&text=Hello%20AL-AMANAT%2C%20I%20am%20interested%20in%20Umrah%20packages`}
                    target="_blank"
                    rel="noreferrer"
                    className="btn-primary"
                  >
                    <FaWhatsapp /> Book Umrah on WhatsApp
                  </a>
                  <a href="#umrah" className="btn-secondary">
                    View Umrah Deals
                  </a>
                </>
              ) : (
                <>
                  <a href={WA_LINK} target="_blank" rel="noreferrer" className="btn-primary">
                    <FaWhatsapp /> Book on WhatsApp
                  </a>
                  <Link to="/services" className="btn-secondary">
                    View Packages
                  </Link>
                </>
              )}
            </div>
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Controls */}
      <button className="hero__arrow hero__arrow--left"  onClick={prev} aria-label="Previous">
        <FaArrowLeft />
      </button>
      <button className="hero__arrow hero__arrow--right" onClick={next} aria-label="Next">
        <FaArrowRight />
      </button>

      {/* Dots */}
      <div className="hero__dots">
        {heroSlides.map((_, i) => (
          <button
            key={i}
            className={`hero__dot ${i === current ? 'hero__dot--active' : ''}`}
            onClick={() => setCurrent(i)}
            aria-label={`Slide ${i + 1}`}
          />
        ))}
      </div>
    </section>
  );
}

// ─── Stats Bar ───────────────────────────────────────────────
function StatsBar() {
  return (
    <section className="stats-bar">
      <div className="container stats-bar__inner">
        {stats.map((s, i) => (
          <div key={i} className="stats-bar__item">
            <span className="stats-bar__value">{s.value}</span>
            <span className="stats-bar__label">{s.label}</span>
          </div>
        ))}
      </div>
    </section>
  );
}

// ─── Services Preview ────────────────────────────────────────
function ServicesSection() {
  return (
    <section className="home-services section-pad">
      <div className="container">
        <div className="section-header">
          <p className="section-label">What We Offer</p>
          <h2 className="section-title">Tours & Travel <span>Services</span></h2>
          <p className="section-desc">
            From Umrah and Hajj pilgrimages to international flight ticketing and hotel bookings — we handle every detail with professionalism.
          </p>
        </div>
        <div className="services-grid">
          {services.map((svc, i) => {
            const Icon = SERVICE_ICONS[svc.icon];
            return (
              <motion.div
                key={svc.id}
                className="svc-card"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.45, delay: i * 0.1 }}
              >
                <div className="svc-card__icon">
                  <Icon size={28} />
                </div>
                <h3 className="svc-card__title">{svc.title}</h3>
                <p  className="svc-card__desc">{svc.desc}</p>
              </motion.div>
            );
          })}
        </div>
        <div className="section-cta">
          <Link to="/services" className="btn-outline">See All Services</Link>
        </div>
      </div>
    </section>
  );
}

// ─── Umrah Packages Preview ──────────────────────────────────
function UmrahSection() {
  return (
    <section className="home-umrah section-pad" id="umrah">
      <div className="container">
        <div className="section-header">
          <p className="section-label">Sacred Pilgrimage</p>
          <h2 className="section-title">Umrah <span>Packages</span></h2>
          <p className="section-desc">
            All-inclusive spiritual journeys to Makkah and Madinah with visa processing, return airline ticket, hotel accommodation, and ground transfers.
          </p>
        </div>

        <div className="umrah-grid">
          {umrahPackages.map((pkg, i) => (
            <motion.div
              key={pkg.id}
              className={`pkg-card ${i === 1 ? 'pkg-card--featured' : ''}`}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.45, delay: i * 0.15 }}
            >
              <span className="pkg-card__badge">All-Inclusive</span>
              <p className="pkg-card__eyebrow">Umrah Pilgrimage</p>
              <h3 className="pkg-card__name">{pkg.name}</h3>
              <p className="pkg-card__intro">{pkg.subtitle}</p>

              <div className="pkg-price-banner">
                <span className="pkg-price-label">Package Fare</span>
                <strong className="pkg-price-val">{pkg.price}</strong>
              </div>

              <ul className="pkg-card__features">
                {pkg.features.map((f, j) => (
                  <li key={j}>
                    <FaCheckCircle className="pkg-check" />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>

              <a
                href={`https://api.whatsapp.com/send?phone=${COMPANY.whatsapp}&text=Hello%20AL-AMANAT%2C%20I%20am%20interested%20in%20booking%20the%20${encodeURIComponent(pkg.name)}%20Umrah%20package.%20Please%20share%20the%20registration%20details.`}
                target="_blank"
                rel="noreferrer"
                className={i === 1 ? 'btn-primary' : 'btn-outline'}
              >
                <FaWhatsapp /> Book on WhatsApp
              </a>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─── Hajj Packages Preview ────────────────────────────────────
function HajjSection() {
  return (
    <section className="home-packages section-pad" id="hajj">
      <div className="container">
        <div className="section-header">
          <p className="section-label">Hajj {hajjCampaign.year}</p>
          <h2 className="section-title">Hajj <span>Packages</span></h2>
          <p className="section-desc">
            Registration is selling fast. Make your initial payment on or before {hajjCampaign.paymentDeadline} to secure your place.
          </p>
        </div>
        <div className="hajj-trip-info" aria-label="Hajj travel dates">
          <div><span>Departure</span><strong>{hajjCampaign.departure}</strong></div>
          <div><span>Return</span><strong>{hajjCampaign.returnDate}</strong></div>
          <div><span>Payment deadline</span><strong>{hajjCampaign.paymentDeadline}</strong></div>
        </div>
        <div className="pkg-grid">
          {hajjPackages.map((pkg, i) => (
            <motion.div
              key={pkg.id}
              className={`pkg-card ${pkg.highlight ? 'pkg-card--featured' : ''}`}
              initial={{ opacity: 0, scale: 0.96 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.45, delay: i * 0.15 }}
            >
              {pkg.badge && (
                <span className="pkg-card__badge">{pkg.badge}</span>
              )}
              <p className="pkg-card__eyebrow">Hajj pilgrimage</p>
              <h3 className="pkg-card__name">{pkg.name}</h3>
              <p className="pkg-card__intro">Choose the room option that suits your journey.</p>
              <div className="pkg-card__prices">
                {pkg.options.map((opt, j) => (
                  <div key={j} className="pkg-price-row">
                    <span className="pkg-price-label">{opt.label}</span>
                    <strong className="pkg-price-val">{opt.price}</strong>
                  </div>
                ))}
              </div>
              <ul className="pkg-card__features">
                {pkg.features.slice(0, 4).map((f, j) => (
                  <li key={j}><FaCheckCircle className="pkg-check" />{f}</li>
                ))}
              </ul>
              <a href={WA_LINK} target="_blank" rel="noreferrer"
                className={pkg.highlight ? 'btn-primary' : 'btn-outline'}
              >
                <FaWhatsapp /> Book Now
              </a>
            </motion.div>
          ))}
        </div>
        <div className="section-cta">
          <Link to="/services" className="btn-outline">Full Package Details</Link>
        </div>
      </div>
    </section>
  );
}

// ─── Testimonials ────────────────────────────────────────────
function Testimonials() {
  return (
    <section className="testimonials section-pad">
      <div className="container">
        <div className="section-header">
          <p className="section-label">Happy Clients</p>
          <h2 className="section-title">What They <span>Say</span></h2>
        </div>
        <div className="testy-grid">
          {testimonials.map((t, i) => (
            <motion.div
              key={t.id}
              className="testy-card"
              initial={{ opacity: 0, x: i === 0 ? -30 : 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.55 }}
            >
              <FaQuoteLeft className="testy-quote" />
              <p className="testy-text">"{t.quote}"</p>
              <div className="testy-stars">
                {[...Array(5)].map((_, j) => <FaStar key={j} />)}
              </div>
              <div className="testy-author">
                <img src={CLIENT_IMGS[i]} alt={t.name} className="testy-avatar" />
                <div>
                  <strong>{t.name}</strong>
                  <span>{t.role}</span>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─── Home Discover ───────────────────────────────────────────
function DiscoverSection() {
  return (
    <section className="home-discover section-pad">
      <div className="container">
        <div className="section-header">
          <p className="section-label">Plan With Confidence</p>
          <h2 className="section-title">More of <span>AL-AMANAT</span></h2>
          <p className="section-desc">Explore our traveller moments, find an office near you, or speak with our team directly.</p>
        </div>
        <div className="discover-grid">
          <Link to="/about#gallery" className="discover-gallery">
            <img src={masjidNew} alt="Pilgrims at the Haram" />
            <img src={paris} alt="International travel" />
            <img src={medina} alt="Madinah journey" />
            <span className="discover-gallery__label"><FaImages /> View our gallery <FaArrowRight /></span>
          </Link>
          <div className="discover-locations">
            <div className="discover-locations__icon"><FaMapMarkerAlt /></div>
            <p className="discover-locations__eyebrow">Visit us in person</p>
            <h3>Six offices across South-West Nigeria</h3>
            <p>Our head office is in Ikeja, Lagos, with branches in Ilorin, Ijebu Ode, Abeokuta, Osogbo, and Ibadan.</p>
            <Link to="/contact#locations" className="discover-link">Find your nearest office <FaArrowRight /></Link>
          </div>
        </div>
      </div>
    </section>
  );
}

// ─── CTA Banner ──────────────────────────────────────────────
function CTABanner() {
  return (
    <section className="cta-banner">
      <div className="cta-banner__bg" />
      <div className="container cta-banner__content">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
        >
          <h2>Ready to Embark on Your Journey?</h2>
          <p>Book your Umrah or Hajj pilgrimage today on WhatsApp or call our team directly — we will plan every detail for you.</p>
          <div className="cta-banner__actions">
            <a href={WA_LINK} target="_blank" rel="noreferrer" className="btn-primary">
              <FaWhatsapp /> Chat on WhatsApp
            </a>
            <Link to="/contact" className="btn-secondary">
              See All Offices
            </Link>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

// ─── Page ─────────────────────────────────────────────────────
export default function Home() {
  return (
    <>
      <HeroCarousel />
      <StatsBar />
      <ServicesSection />
      <UmrahSection />
      <HajjSection />
      <Testimonials />
      <DiscoverSection />
      <CTABanner />
    </>
  );
}
